import React, { useState, Suspense, lazy } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { BsPencilSquare  } from "react-icons/bs";
import { MdDelete, MdOutlineScheduleSend } from "react-icons/md";
import { FaPlus, FaShareSquare, FaUndo, FaChevronDown, FaCodeBranch, FaPaperPlane, FaArrowLeft, FaPrint, FaCopy, FaEye, FaRegLightbulb } from 'react-icons/fa';
import { ActionDropdown, Button, LoadingSpinner } from 'components/common';
import ChangeOrderStatus from './ChangeOrderStatus';
import ChangeOrderDetailMessageAction from './ChangeOrderDetailMessageAction';
import PropTypes from 'prop-types';
import { downloadCOForm } from 'redux/crm/project/changeOrderSlice';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import ChangeOrderRevisionForm from '../../change-order-revision/ChangeOrderRevisionForm';

const LazyCopyPIFForm = lazy(() => import('components/app/crm/pif/CopyPIFForm'));
const LazyChangeOrderDeleteConfirm = lazy(() => import('./ChangeOrderDeleteConfirm'));
const LazyChangeOrderRequestConfirmToCSD = lazy(() => import('./confirmation/ChangeOrderRequestConfirmToCSD'));
const LazyChangeOrderRevoke = lazy(() => import('./confirmation/ChangeOrderRevoke'));
const LazyChangeOrderRequestFullSchedule = lazy(() => import('./ChangeOrderFinalRequestSchedule'));
const LazyChangeOrderSendFullScheduleToSales = lazy(() => import('./confirmation/ChangeOrderSendFullScheduleToSales'));
const LazyChangeOrderLock = lazy(() => import('./confirmation/ChangeOrderLock'));
const LazyChangeOrderUnLock = lazy(() => import('./confirmation/ChangeOrderUnLock'));
const LazyChangeOrderQuickGuide = lazy(() => import('./ChangeOrderQuickGuide'));

const ChangeOrderDetailHeader = ({changeOrder}) => {
  const [showChangeOrderDeleteConfirm, setShowChangeOrderDeleteConfirm] = useState(false);
  const [showCopyPIFForm, setShowCopyPIFForm] = useState(false);
  const [requestConfirmationToCSD, setRequestConfirmationToCSD] = useState(false);
  const [revokeConfirmation, setRevokeConfirmation] = useState(false);

  const [requestFullSchedule, setRequestFullSchedule] = useState(false);
  const [titleFullSchedule, setTitleFullSchedule] = useState("");
  const [isSchedule, setIsSchedule] = useState(false);

  const [sendFullScheduleToSales, setSendFullScheduleToSales] = useState(false);
  const [lockCO, setLockCO] = useState(false);
  const [unlockCO, setUnlockCO] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showRevisionForm, setShowRevisionForm] = useState(false);
  const [showQuickGuide, setShowQuickGuide] = useState(false);

  const {project, building, permissions} = changeOrder || {};
  const navigate = useNavigate();
  
  const projectId = project?.id;
  const buildingId = building?.id;
  const changeOrderId = changeOrder?.id;

  const projectUrl = "/crm/projects";
  const changeOrderUrl = `${projectUrl}/${projectId}/buildings/${buildingId}/change-orders`;
  const pifUrl = `${projectUrl}/${projectId}/buildings/${buildingId}/adaptations/${changeOrderId}/pifs`;

  const pifId = changeOrder?.pif_id;
  const dispatch = useDispatch();

  const {
    allow_create_co,
    allow_edit_co_form,
    allow_delete,
    allow_request_confirm,
    allow_revoke,
    allow_create_pif,
    allow_view_pif,
    allow_copy_pif,
    allow_final_enter_job_request,
    allow_final_enter_job_schedule,
    allow_final_enter_job_release,
    allow_create_co_revision
  } = permissions || {}

  // const handlePrintCO = () => {
  //   navigate(`${changeOrderUrl}/${changeOrderId}/print`, {state: {changeOrder}})
  // }

  const handlePrintCO = async () => {
    try {
      setLoading(true)
      await dispatch(downloadCOForm({
        projectId,
        buildingId,
        changeOrderId,
      })).unwrap();
    }
    catch(error) {
      toast.error(error.message);
    }
    finally {
      setLoading(false)
    }
  }

  const handleEditCOForm= () => {
    navigate(`${changeOrderUrl}/${changeOrderId}/edit`, {state: {changeOrder}})
  }

  const handleNewPIF = () => {
    navigate(`${pifUrl}/new-pif`, {state: {project, building, adaptation: changeOrder}})
  }

  const handleViewPIF = () => {
    window.open(`${pifUrl}/${pifId}?type=change-order`, '_blank');
  }

  const handleCopyPIF = () => {
    setShowCopyPIFForm(true)
  }

  const handleProject = () => {
    navigate(`${projectUrl}/${projectId}`)
  }

  const handleDeleteChangeOrder= () => {
    setShowChangeOrderDeleteConfirm(true)
  }

  const handleRequestConfirmToCSD = () => {
    setRequestConfirmationToCSD(true)
  }

  const handleRevokeCO = () => {
    setRevokeConfirmation(true)
  }

  const handleRequestScheduleToENG = () => {
    setRequestFullSchedule(true)
    setTitleFullSchedule("Request schedule to ENG")
    setIsSchedule(false)
  }

  const handleSendScheduleToCSD = () => {
    setRequestFullSchedule(true)
    setTitleFullSchedule("Submit schedule to CSD")
    setIsSchedule(true)
  }

  const handleSendScheduleToSales = () => {
    setSendFullScheduleToSales(true)
  }

  // const handleLockCO = () => {
  //   setLockCO(true)
  // }

  const handleNewChangeOrder = () => {
    navigate(`${changeOrderUrl}/new`, {state: {project, building}});
  }

  const handleNewChangeOrderRevision = () => {
    setShowRevisionForm(true)
  }

  // CSD stops further REV# on this CO#; any later change needs a new CO#.
  // Action menu — grouped by intent with semantic icon colors. Each group is an
  // array of items; empty groups are dropped and the rest joined with dividers.
  // "Request Revise CO" was retired: revisions replace it (New Revision fires on
  // the building's latest accepted CO# that CSD has not blocked).
  const iconStyle = "tw-text-[15px]";
  const actionGroups = [
    [
      allow_create_co && {
        label: 'New Change order',
        icon: <FaPlus className={`tw-text-emerald-500 ${iconStyle}`} />,
        onClick: handleNewChangeOrder
      },
      allow_create_co_revision && {
        label: 'New Revision',
        icon: <FaCodeBranch className={`tw-text-emerald-500 ${iconStyle}`} />,
        onClick: handleNewChangeOrderRevision
      }
    ],
    [
      allow_request_confirm && {
        label: 'Request confirmation to CSD',
        icon: <FaShareSquare className={`tw-text-blue-500 ${iconStyle}`} />,
        onClick: handleRequestConfirmToCSD
      },
      allow_final_enter_job_request && {
        label: 'Request schedule to ENG',
        icon: <FaPaperPlane className={`tw-text-primary-500 ${iconStyle}`} />,
        onClick: handleRequestScheduleToENG
      },
      allow_final_enter_job_schedule && {
        label: 'Send schedule to CSD',
        icon: <MdOutlineScheduleSend className={`tw-text-primary-500 ${iconStyle}`} />,
        onClick: handleSendScheduleToCSD
      },
      allow_final_enter_job_release && {
        label: 'Send schedule to Sales',
        icon: <MdOutlineScheduleSend className={`tw-text-primary-500 ${iconStyle}`} />,
        onClick: handleSendScheduleToSales
      }
    ],
    [
      allow_revoke && {
        label: 'Revoke previous step',
        icon: <FaUndo className={`tw-text-amber-500 ${iconStyle}`} />,
        onClick: handleRevokeCO
      },
      allow_edit_co_form && {
        label: 'Edit change order form',
        icon: <BsPencilSquare className={`tw-text-slate-500 ${iconStyle}`} />,
        onClick: handleEditCOForm
      }
    ],
    [
      allow_delete && {
        label: 'Delete CO',
        icon: <MdDelete className={iconStyle} />,
        danger: true,
        onClick: handleDeleteChangeOrder
      }
    ]
  ];

  const actionItems = actionGroups
    .map(group => group.filter(Boolean))
    .filter(group => group.length > 0)
    .flatMap((group, index) => (index === 0 ? group : [{ divider: true }, ...group]));

  return (
    <>
      <div className="tw-mb-3 tw-bg-white tw-border tw-border-solid tw-border-slate-200 tw-rounded tw-overflow-hidden">
        <div className="tw-flex tw-justify-between tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-border-0 tw-border-b tw-border-solid tw-border-slate-200 tw-bg-slate-50">
          <div className="tw-flex tw-items-center tw-gap-1">
            <Button
              onClick={() => navigate(-1)}
              variant="secondary"
              size="md"
              leftIcon={FaArrowLeft}
              title="Back"
            />
            <Button
              variant="secondary"
              size="md"
              onClick={handleProject}
            >
              <span className="tw-hidden xl:tw-inline-block">Back to Project</span>
            </Button>
          </div>
          <div className="tw-flex tw-items-center tw-gap-1">
            <Button
              variant="guide"
              size="md"
              leftIcon={FaRegLightbulb}
              title="Quick guide — how to use this change order"
              onClick={() => setShowQuickGuide(true)}
            >
              <span className="tw-hidden xl:tw-inline-block">Guide</span>
            </Button>

            {changeOrder?.co !== "00" && (
              <Button
                variant="secondary"
                size="md"
                leftIcon={FaPrint}
                onClick={handlePrintCO}
              >
                <span className="tw-hidden xl:tw-inline-block">Change order form</span>
              </Button>
            )}

            {pifId === null ? (
              <>
                {allow_create_pif && (
                  <Button
                    variant="secondary"
                    size="md"
                    leftIcon={FaPlus}
                    onClick={handleNewPIF}
                  >
                    <span className="tw-hidden xl:tw-inline-block">New PIF</span>
                  </Button>
                )}

                {allow_copy_pif && (
                  <Button
                    variant="secondary"
                    size="md"
                    leftIcon={FaCopy}
                    onClick={handleCopyPIF}
                  >
                    <span className="tw-hidden xl:tw-inline-block">Copy PIF</span>
                  </Button>
                )}
              </>
            ) : (
              <Button
                variant="secondary"
                size="md"
                leftIcon={FaEye}
                onClick={handleViewPIF}
                disabled={!allow_view_pif}
              >
                <span className="tw-hidden xl:tw-inline-block">View Final PIF</span>
              </Button>
            )}

            {actionItems.length > 0 && (
              <ActionDropdown
                align="end"
                menuWidth={240}
                items={actionItems}
                triggerClassName="!tw-w-auto !tw-h-9 !tw-px-3.5 !tw-gap-1.5 !tw-ml-1 !tw-bg-blue-600 hover:!tw-bg-blue-700 !tw-border-blue-600 !tw-text-white !tw-font-semibold !tw-text-sm"
                trigger={<><span>Action</span><FaChevronDown size={11} /></>}
              />
            )}
          </div>
        </div>

        {loading && (
          <LoadingSpinner size="sm" message="Downloading..." className="!tw-py-2" />
        )}

        <div className="tw-p-4 tw-text-xs">
          <h5 className="tw-text-center tw-text-xl tw-font-semibold tw-uppercase tw-text-slate-900">Change Order Detail</h5>
          <div className="tw-text-center tw-mb-2">No.: <span className="tw-text-blue-600 tw-font-bold">{changeOrder?.id}</span></div>

          <div className="tw-grid tw-grid-cols-2 tw-gap-x-3 tw-gap-y-1">
            <div className="tw-text-right">Project name :</div>
            <div><Link to={`/crm/projects/${projectId}`} target="_blank">{project?.name}</Link></div>

            <div className="tw-text-right">Building :</div>
            <div className="tw-text-blue-600 tw-font-bold">{building?.name}-CO#{changeOrder?.co} ({project?.j_number})</div>

            <div className="tw-text-right">Status :</div>
            <div>
              <ChangeOrderStatus status={changeOrder?.co_status} />
            </div>
          </div>
        </div>
      </div>

      <ChangeOrderDetailMessageAction changeOrder={changeOrder} />

      <div className="tw-my-2 tw-px-4 tw-text-sm tw-flex tw-justify-start tw-items-center tw-flex-wrap tw-gap-1">
        <span className="tw-mr-1 tw-font-bold">Next CO#:</span> {changeOrder?.list_change_orders && changeOrder?.list_change_orders.map(item => (
          <Link
            to={`${changeOrderUrl}/${item.id}`}
            className="tw-inline-flex tw-items-center tw-px-3 tw-py-1.5 tw-border tw-border-solid tw-border-slate-200 tw-rounded tw-no-underline hover:tw-bg-slate-50"
            style={
              changeOrder?.id === item.id
                ? { color: '#2c7be5', fontWeight: 'bold' }
                : {}
            }
            key={item.id}
          >
            {item.co}
          </Link>
        )) }
      </div>

      <Suspense fallback={<div>Loading...</div>}>
        {showCopyPIFForm && (
          <LazyCopyPIFForm
            showModal={showCopyPIFForm}
            setshowModal={setShowCopyPIFForm}
            project={project}
            building={building}
            adaptation={changeOrder}
            type="enter-job"
          />
        )}

        {showChangeOrderDeleteConfirm && (
          <LazyChangeOrderDeleteConfirm
            showModal={showChangeOrderDeleteConfirm}
            setshowModal={setShowChangeOrderDeleteConfirm}
            changeOrder={changeOrder}
          />
        )}

        {requestConfirmationToCSD && (
          <LazyChangeOrderRequestConfirmToCSD
            showModal={requestConfirmationToCSD}
            setshowModal={setRequestConfirmationToCSD}
            changeOrder={changeOrder}
          />
        )}

        {revokeConfirmation && (
          <LazyChangeOrderRevoke
            showModal={revokeConfirmation}
            setshowModal={setRevokeConfirmation}
            changeOrder={changeOrder}
          />
        )}

        {requestFullSchedule && (
          <LazyChangeOrderRequestFullSchedule
            showModal={requestFullSchedule}
            setshowModal={setRequestFullSchedule}
            changeOrder={changeOrder}
            title={titleFullSchedule}
            isScheduled={isSchedule}
          />
        )}

        {sendFullScheduleToSales && (
          <LazyChangeOrderSendFullScheduleToSales
            showModal={sendFullScheduleToSales}
            setshowModal={setSendFullScheduleToSales}
            changeOrder={changeOrder}
          />
        )}

        {lockCO && (
          <LazyChangeOrderLock
            showModal={lockCO}
            setshowModal={setLockCO}
            changeOrder={changeOrder}
          />
        )}

        {unlockCO && (
          <LazyChangeOrderUnLock
            showModal={unlockCO}
            setshowModal={setUnlockCO}
            changeOrder={changeOrder}
          />
        )}

        {showRevisionForm && (
          <ChangeOrderRevisionForm
            showModal={showRevisionForm}
            setshowModal={setShowRevisionForm}
            project={project}
            building={building}
            changeOrder={changeOrder}
          />
        )}

        {showQuickGuide && (
          <LazyChangeOrderQuickGuide
            show={showQuickGuide}
            onHide={() => setShowQuickGuide(false)}
            changeOrder={changeOrder}
          />
        )}
      </Suspense>
    </>
  );
};

ChangeOrderDetailHeader.propTypes = {
  changeOrder: PropTypes.object.isRequired
}

export default ChangeOrderDetailHeader;